import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getBrowserLocalTimestamp } from "@shared/browser-local-time";
import { buildSessionExpiredLoginPath } from "@shared/session-expiration";

import { TRPC_SAFETY_CONFIG } from "./lib/trpcSafetyConfig";
import { shouldEvictInactiveQuery } from "./lib/sessionCachePolicy";
import { isPublicLaunchPath, normalizeStandaloneLaunch } from "./lib/desktopLaunch";
import "./index.css";

// Installed desktop launches must never reopen a stale setup page, tenant
// workspace, or tenant return URL. In-app navigation after login is unchanged.
normalizeStandaloneLaunch();

// Optimize QueryClient configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: TRPC_SAFETY_CONFIG.staleTime.MEDIUM,
      gcTime: TRPC_SAFETY_CONFIG.gcTime.MEDIUM,
      retry: 1,
      retryDelay: 300,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Reduce network overhead on mobile
      networkMode: 'always',
    },
    mutations: {
      retry: 1,
      // Reduce network overhead on mobile
      networkMode: 'always',
    },
  },
});

// Monitor the query cache without evicting active session queries during tab navigation.
// Removing a query triggers another cache event, so pruning must be guarded to avoid
// recursive work while a dense workspace view is mounting (notably in Safari).
let isPruningInactiveQueries = false;
let lastInactiveQueryPruneAt = 0;
const cacheSubscription = queryClient.getQueryCache().subscribe(() => {
  if (isPruningInactiveQueries) return;
  const queries = queryClient.getQueryCache().getAll();

  // Feature tabs can legitimately mount many queries. Only clear inactive,
  // non-session data after a generous age threshold; never clear active auth.
  const now = Date.now();
  if (queries.length > 150 && now - lastInactiveQueryPruneAt >= 60_000) {
    isPruningInactiveQueries = true;
    try {
    queries.forEach((query) => {
      if (shouldEvictInactiveQuery({ queryKey: query.queryKey, observerCount: query.getObserversCount(), updatedAt: query.state.dataUpdatedAt || 0, now })) {
        queryClient.removeQueries({ queryKey: query.queryKey });
      }
    });
    lastInactiveQueryPruneAt = now;
    } finally {
      isPruningInactiveQueries = false;
    }
  }
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;
  if (!isUnauthorized) return;

  // Public marketing and intake pages intentionally work without a session.
  // An unauthenticated error from an optional public-page query or diagnostic
  // mutation must never turn the landing page into a session-expired screen.
  if (isPublicLaunchPath(window.location.pathname)) return;

  try {
    // Redirect to the login page with the one-time expiry notice while
    // preserving a safe workspace destination for the next successful login.
    window.location.href = buildSessionExpiredLoginPath(
      `${window.location.pathname}${window.location.search}`,
    );
  } catch (e) {
    // Silently fail - don't break the app
  }
};

// Optimize error handling - only redirect on auth errors
queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    if (error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG) {
      redirectToLoginIfUnauthorized(error);
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    if (error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG) {
      redirectToLoginIfUnauthorized(error);
    }
  }
});

// Optimize tRPC client with better batching and connection settings
const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
		headers() {
			const { localDateTime, timezone } = getBrowserLocalTimestamp();
			const tenantFromPath = typeof window !== "undefined"
				? window.location.pathname.match(/^\/tenant\/([^/?#]+)/)?.[1]
				: undefined;
			const tenantFromReturn = typeof window !== "undefined"
				? new URLSearchParams(window.location.search).get("return")?.match(/^\/tenant\/([^/?#]+)/)?.[1]
				: undefined;
			const workspaceSlug = tenantFromPath || tenantFromReturn;
			return {
				"x-tracklabs-local-datetime": localDateTime,
				"x-tracklabs-timezone": timezone,
				...(workspaceSlug ? { "x-tracklabs-workspace": workspaceSlug } : {}),
			};
		},
      // Batch requests more aggressively for better performance
      maxURLLength: 2000,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
          // Add priority hints for faster loading
          priority: "high" as RequestInit['priority'],
        });
      },
    }),
  ],
});

// Render app with optimized configuration
createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
